import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AbstractStateInterface } from '../../interface/abstract';
import { InterfaceToSearchParams } from '../../utils/helper';
import { ApiService } from '../../utils/apiService';
import { PaintingInterface } from '../../interface/painting';

type DataState = AbstractStateInterface<PaintingInterface>;

export interface PaintingSearch {
  id?: string;
}
const initialState: DataState = {
  data: [],
  detail: null,
  loading: false,
};

export const findPaintingByCondition = createAsyncThunk(
  'admin-api/painting/get',
  async (payload: PaintingSearch, thunkAPI) => {
    const searchParams = InterfaceToSearchParams(payload);
    try {
      const response = await ApiService(`admin-api/painting?${searchParams}`, 'GET', null);
      if (response.statusCode !== 200) {
        return thunkAPI.rejectWithValue({
          code: response.statusCode,
          error: response.error || 'Login failed',
        });
      }
      return {
        code: response.statusCode,
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        code: error.response?.status || 500,
        error: error.message || 'Error',
      });
    }
  },
);
export const getAllPaintings = createAsyncThunk('admin-api/painting/all', async (_, thunkAPI) => {
  try {
    const response = await ApiService(`admin-api/painting/all`, 'GET', null);
    if (response.statusCode !== 200) {
      return thunkAPI.rejectWithValue({
        code: response.statusCode,
        error: response.error || 'Login failed',
      });
    }
    return {
      code: response.statusCode,
      data: response.data,
      message: response.message,
    };
  } catch (error: any) {
    return thunkAPI.rejectWithValue({
      code: error.response?.status || 500,
      error: error.message || 'Error',
    });
  }
});

export const createPainting = createAsyncThunk('admin-api/painting', async (payload: FormData, thunkAPI) => {
  try {
    const response = await ApiService(`admin-api/painting`, 'POST', payload);
    if (response.statusCode !== 200) {
      return thunkAPI.rejectWithValue({
        code: response.statusCode,
        error: response.error || 'Login failed',
      });
    }
    return {
      code: response.statusCode,
      data: response.data,
      message: response.message,
    };
  } catch (error: any) {
    return thunkAPI.rejectWithValue({
      code: error.response?.status || 500,
      error: error.message || 'Error',
    });
  }
});

export const updatePainting = createAsyncThunk('admin-api/painting/put', async (payload: FormData, thunkAPI) => {
  try {
    const response = await ApiService(`admin-api/painting`, 'PUT', payload);
    if (response.statusCode !== 200) {
      return thunkAPI.rejectWithValue({
        code: response.statusCode,
        error: response.error || 'Login failed',
      });
    }
    return {
      code: response.statusCode,
      data: response.data,
      message: response.message,
    };
  } catch (error: any) {
    return thunkAPI.rejectWithValue({
      code: error.response?.status || 500,
      error: error.message || 'Error',
    });
  }
});

export const deletePainting = createAsyncThunk(
  'admin-api/painting/delete',
  async (payload: PaintingSearch, thunkAPI) => {
    const searchParams = InterfaceToSearchParams(payload);
    try {
      const response = await ApiService(`admin-api/painting?${searchParams}`, 'DELETE', null);
      if (response.statusCode !== 200) {
        return thunkAPI.rejectWithValue({
          code: response.statusCode,
          error: response.error || 'Login failed',
        });
      }
      return {
        code: response.statusCode,
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        code: error.response?.status || 500,
        error: error.message || 'Error',
      });
    }
  },
);

const paintingSlice = createSlice({
  name: 'painting',
  initialState,
  reducers: {
    resetPaintingSlice: () => {
      return { ...initialState };
    },
    resetDetail: (state) => {
      state.detail = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllPaintings.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllPaintings.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(action.payload.data)) {
          state.data = action.payload.data as PaintingInterface[];
        } else {
          state.data = [];
        }
      })
      .addCase(getAllPaintings.rejected, (state) => {
        state.loading = false;
      })
      .addCase(findPaintingByCondition.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(action.payload.data)) {
          state.detail = action.payload.data[0] as PaintingInterface;
        } else {
          state.detail = null;
        }
      })
      .addCase(findPaintingByCondition.rejected, (state) => {
        state.loading = false;
      });
  },
});
export const { resetPaintingSlice, resetDetail } = paintingSlice.actions;
export default paintingSlice.reducer;
